export function safeStringify(value: any, space = 2): string {
  const seen = new WeakSet()

  return JSON.stringify(
    value,
    function replacer(key, val) {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]'
        seen.add(val)
      }

      if (typeof val === 'bigint') {
        return `${val.toString()}n`
      }

      if (val instanceof Map) {
        return {
          type: 'Map',
          value: Array.from(val.entries())
        }
      }

      if (val instanceof Set) {
        return {
          type: 'Set',
          value: Array.from(val.values())
        }
      }

      if (typeof val === 'function' || typeof val === 'undefined') {
        return undefined
      }

      return val
    },
    space
  )
}

type LogLevel = 'info' | 'warn' | 'error' | 'log' | 'debug'

/**
 * Logs a message to the console with a specified log level.
 * @param {LogLevel} level - The log level ('info', 'warn', 'error', 'debug').
 * @param {string} message - The message to log.
 * @param {any} [data] - Optional additional data to log. This can be an object, array, or any other type and will be stringified.
 * @param {string} [tag] - Optional tag to categorize the log.
 * @example: log('log', 'This is a log message', { key: 'value' }, 'MyTag')
 * @example: log('info', 'Server started', null, 'Server')
 * @example: log('warn', 'Low disk space', { available: '500MB' }, 'System')
 * @example: log('error', 'Unhandled exception', new Error('Something went wrong'), 'Menu')
 * @example: log('debug', 'Debugging info', ['input1', 'input2'], 'InputValidator')
 * @returns {void}
 */
// eslint-disable-next-line
function log(level: LogLevel, message: string, data?: any, tag?: string) {
  const output = [`[${level.toUpperCase()}]`, tag ? `[${tag}]` : '', message, data ? `\n${safeStringify(data)}` : '']
    .filter(Boolean)
    .join(' ')

  switch (level) {
    case 'info':
      console.info(output)
      break
    case 'warn':
      console.warn(output)
      break
    case 'error':
      console.error(output)
      break
    case 'debug':
      console.debug(output)
      break
    default:
      console.log(output)
  }
}

// eslint-disable-next-line
export const logInfo = (msg: string, data?: any, tag?: string) => log('info', msg, data, tag)

// eslint-disable-next-line
export const logWarn = (msg: string, data?: any, tag?: string) => log('warn', msg, data, tag)

// eslint-disable-next-line
export const logError = (msg: string, data?: any, tag?: string) => log('error', msg, data, tag)

// eslint-disable-next-line
export const logDebug = (msg: string, data?: any, tag?: string) => log('debug', msg, data, tag)
